// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form component for resolving date-related asset exceptions.
 *
 * Displays two types of date exceptions:
 * 1. Simple dates (inventory date, in-service date):
 *    - Editable date picker fields with inline save buttons
 *    - Each field can be saved independently
 *    - Loading/success/error feedback per field
 *
 * 2. Complex dates (sale date, scrap date, assignment date, movement date):
 *    - Informational message directing users to resolve on asset details page
 *    - Link to asset details page
 *    - Explanation of why resolution must happen elsewhere
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { updateAsset } from '@/asset/api/update-asset';
import { updateAssetAccountingProperties } from '@/asset/api/update-asset-accounting-properties';
import { FormDatePicker } from '@/components/form-date-picker';
import { ExceptionType } from '@/constants/asset-exception-type';
import {
  CalendarToday,
  CheckCircle,
  Error,
  OpenInNew,
} from '@/constants/hardfin-standard-icons';
import { DateExceptionFormData } from '@/report/api/types';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import { pluralize } from '@/utilities/pluralize';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';

/**
 * Maps backend error codes to the corresponding form field names.
 * Used to display field-specific error messages from the API.
 */
const ERROR_CODE_TO_FIELD: Record<number, keyof DateExceptionFormData> = {
  [ERROR_CODE_MAPPING.asset_invalid_inventory_date]: 'inventoryDate',
  [ERROR_CODE_MAPPING.accounting_invalid_in_service_date]: 'inServiceDate',
};

/**
 * Props for the DateExceptionResolutionForm component.
 */
interface Props {
  /**
   * Array of exception types to display and resolve.
   * Each exception will be shown with relevant date fields.
   */
  exceptionTypes: ExceptionType[];

  /**
   * React Hook Form control for managing form state.
   */
  control: Control<DateExceptionFormData>;

  /**
   * The asset key used for navigating to the asset details page
   * and making individual field update API calls.
   */
  assetKey: string;

  /**
   * The asset serial number used in informational messages.
   */
  assetSerial: string;

  /**
   * Date data for the asset containing all date fields.
   * Used to display current date values in complex exception messages.
   */
  dateData: {
    inventoryDate: DateTime;
    inServiceDate: DateTime | null;
    scrapDate: DateTime | null;
    firstSaleDate: DateTime | null;
    firstAssignmentDate: DateTime | null;
    firstMovementDate: DateTime | null;
  };
}

/**
 * Simple date exception types that can be edited inline with individual save buttons.
 */
const SIMPLE_EXCEPTION_TYPES: ExceptionType[] = [
  'IN_SERVICE_DATE_BEFORE_INVENTORY',
];

/**
 * Complex date exception types that require resolution on the asset details page.
 */
const COMPLEX_EXCEPTION_TYPES: ExceptionType[] = [
  'FIRST_SALE_DATE_BEFORE_INVENTORY',
  'DISPOSAL_DATE_BEFORE_INVENTORY',
  'FIRST_ASSIGNMENT_BEFORE_INVENTORY',
  'FIRST_MOVEMENT_BEFORE_INVENTORY',
];

/**
 * Configuration for complex exceptions explaining why they need to be resolved elsewhere.
 */
const COMPLEX_EXCEPTION_CONFIG: Partial<
  Record<
    ExceptionType,
    {
      reason: string;
    }
  >
> = {
  FIRST_SALE_DATE_BEFORE_INVENTORY: {
    reason: 'Sale date comes from ownership records',
  },
  DISPOSAL_DATE_BEFORE_INVENTORY: {
    reason: 'Scrap date comes from disposal records',
  },
  FIRST_ASSIGNMENT_BEFORE_INVENTORY: {
    reason: 'Assignment date comes from project assignments',
  },
  FIRST_MOVEMENT_BEFORE_INVENTORY: {
    reason: 'Movement date comes from movement history',
  },
};

/**
 * Determines if an exception has been resolved by comparing dates.
 * An exception is resolved when the exception-specific date is on or after the inventory date.
 *
 * @param inventoryDate - The inventory date to compare against
 * @param exceptionDate - The exception-specific date to validate
 * @returns true if the exception is resolved (exceptionDate >= inventoryDate), false otherwise
 */
function isExceptionResolved(
  inventoryDate: DateTime | null,
  exceptionDate: DateTime | null
): boolean {
  // If the exception date is not set, it is resolved
  if (!exceptionDate) {
    return true;
  }

  // If the inventory date is not set, it is not resolved
  if (!inventoryDate) {
    return false;
  }

  // Check if both dates are valid
  if (!inventoryDate.isValid || !exceptionDate.isValid) {
    return false;
  }

  return exceptionDate >= inventoryDate;
}

function isSameDate(
  value: DateTime | null | undefined,
  otherValue: DateTime | null | undefined
) {
  if (value === null || value === undefined) {
    return otherValue === null || otherValue === undefined;
  }

  if (otherValue === null || otherValue === undefined) {
    return false;
  }

  return value.toISODate() === otherValue.toISODate();
}

/**
 * Component for rendering an editable date field with an inline save button.
 */
function EditableDateField(props: {
  fieldName: keyof DateExceptionFormData;
  fieldLabel: string;
  control: Control<DateExceptionFormData>;
  assetKey: string;
  originalValue: DateTime | null;
  onSaveSuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const currentValue =
    useWatch({
      control: props.control,
      name: props.fieldName,
    }) ?? null;

  // Track the last successfully saved value
  const [lastSavedValue, setLastSavedValue] = useState<DateTime | null>(
    props.originalValue
  );

  // Track error state for save failures
  const [hasError, setHasError] = useState(false);
  // Track the value at the time of error to detect changes after error
  const [errorValue, setErrorValue] = useState<DateTime | null>(null);
  // Track the error message from the backend
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine if the value has changed from the last saved value
  const hasChanged = !isSameDate(currentValue, lastSavedValue);

  // Clear error state when user changes the value after an error occurred
  useEffect(() => {
    if (hasError && !isSameDate(currentValue, errorValue)) {
      setHasError(false);
      setErrorValue(null);
      setErrorMessage(null);
    }
  }, [currentValue, hasError, errorValue]);

  // Create mutation for saving individual field
  const mutation = useMutation({
    mutationFn: async (value: DateTime | null) => {
      if (props.fieldName === 'inventoryDate') {
        await updateAsset(props.assetKey, {
          inventoryDate: value ?? undefined,
        });
      } else if (props.fieldName === 'inServiceDate') {
        await updateAssetAccountingProperties(props.assetKey, {
          inServiceDate: value,
        });
      }
    },
    onSuccess: () => {
      // Update the last saved value to match what was just saved
      setLastSavedValue(currentValue);
      // Clear any error state
      setHasError(false);
      setErrorValue(null);
      setErrorMessage(null);
      // Invalidate the single-asset query so the dialog can detect resolution
      // Note: We intentionally don't invalidate the main date-inconsistencies query
      // here to avoid query thrashing when resolving multiple assets quickly.
      // The main query refreshes on tab switch or window focus.
      void queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey],
      });
      // Invalidate only date-inconsistencies queries that are filtered by assetIds.
      // This allows the dialog's single-asset query to refresh without triggering
      // a full refetch of the unfiltered main query (which is expensive with 55k+ rows).
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          if (
            key[0] === 'report' &&
            key[1] === 'exception' &&
            key[2] === 'asset' &&
            key[3] === 'date-inconsistencies'
          ) {
            const params = key[4] as { assetIds?: string[] } | undefined;
            return (
              Array.isArray(params?.assetIds) && params.assetIds.length > 0
            );
          }
          return false;
        },
      });
      props.onSaveSuccess();
    },
    onError: (error) => {
      // Extract error information from the backend response
      let backendErrorMessage = 'Save failed, please try again';

      if (error instanceof AxiosError) {
        const wellFormedError = handleAPIError(error);
        const errorCode = wellFormedError.apiStatusCode;

        // Check if this error code applies to the current field
        const fieldForError = ERROR_CODE_TO_FIELD[errorCode];
        if (fieldForError === props.fieldName && wellFormedError.message) {
          // Use the backend error message for this field
          backendErrorMessage =
            wellFormedError.message.charAt(0).toUpperCase() +
            wellFormedError.message.slice(1);
        }
      }

      setHasError(true);
      setErrorValue(currentValue);
      setErrorMessage(backendErrorMessage);
    },
  });

  const handleSave = () => {
    mutation.mutate(currentValue);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box flex={1}>
        <Stack spacing={0}>
          <Box
            sx={
              hasError
                ? {
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'error.main',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'error.main',
                    },
                  }
                : undefined
            }
          >
            <FormDatePicker
              name={props.fieldName}
              label={props.fieldLabel}
              control={props.control}
              disableFuture
            />
          </Box>
          {hasError && errorMessage && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{ marginTop: 0.5, marginLeft: 1.75 }}
            >
              {errorMessage}
            </Typography>
          )}
        </Stack>
      </Box>
      <Tooltip
        arrow
        title={!hasChanged ? 'No changes to save' : ''}
        placement="top"
      >
        <Box component="span" sx={{ alignSelf: 'flex-start', display: 'flex' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={mutation.isPending || !hasChanged}
            aria-label={!hasChanged ? 'No changes to save' : 'Save'}
          >
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Tooltip>
    </Stack>
  );
}

/**
 * Component for rendering informational message for complex date exceptions
 * with live validation based on the inventory date field value.
 */
function ComplexDateExceptionMessage(props: {
  exceptionType: ExceptionType;
  assetKey: string;
  assetSerial: string;
  exceptionDate: DateTime | null;
  control: Control<DateExceptionFormData>;
}) {
  const config = getExceptionConfig(props.exceptionType);
  const complexConfig = COMPLEX_EXCEPTION_CONFIG[props.exceptionType];

  // Watch the current inventory date value from the form
  const currentInventoryDate =
    useWatch({
      control: props.control,
      name: 'inventoryDate',
    }) ?? null;

  // Determine if the exception is resolved based on the watched value
  const resolved = isExceptionResolved(
    currentInventoryDate,
    props.exceptionDate
  );

  if (!complexConfig) {
    return null;
  }

  const iconColor = resolved ? 'success.main' : 'warning.main';
  const borderColor = resolved ? 'success.main' : 'warning.main';
  const backgroundColor = resolved ? 'success.50' : 'warning.50';

  // Get the exception-specific date label
  const exceptionDateLabel = (() => {
    switch (props.exceptionType) {
      case 'FIRST_SALE_DATE_BEFORE_INVENTORY':
        return 'Sale date';
      case 'DISPOSAL_DATE_BEFORE_INVENTORY':
        return 'Scrap date';
      case 'FIRST_ASSIGNMENT_BEFORE_INVENTORY':
        return 'Assignment date';
      case 'FIRST_MOVEMENT_BEFORE_INVENTORY':
        return 'Movement date';
      default:
        return 'Exception date';
    }
  })();

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: 2,
        backgroundColor,
        borderColor,
        borderWidth: 2,
      }}
    >
      <Stack spacing={2}>
        {/* Exception header with icon and label */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: iconColor,
            }}
          >
            {config.icon}
          </Box>
          <Typography variant="subtitle2" color={iconColor}>
            {config.label}
          </Typography>
        </Stack>

        {/* Date comparison display */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Inventory date
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {currentInventoryDate
                ? currentInventoryDate.toLocaleString(DateTime.DATE_MED)
                : 'Not set'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            →
          </Typography>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              {exceptionDateLabel}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {props.exceptionDate
                ? props.exceptionDate.toLocaleString(DateTime.DATE_MED)
                : 'Not set'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {props.exceptionDate &&
            currentInventoryDate &&
            props.exceptionDate.diff(currentInventoryDate, 'days').days < 0
              ? '(' +
                pluralize(
                  Math.abs(
                    props.exceptionDate.diff(currentInventoryDate, 'days').days
                  ),
                  { singular: 'day' }
                ) +
                ' earlier)'
              : ''}
          </Typography>
        </Stack>

        {/* Combined validation and informational message */}
        <Alert
          severity={resolved ? 'success' : 'warning'}
          icon={resolved ? <CheckCircle /> : <Error />}
        >
          <Typography variant="body2">
            {resolved ? (
              'Date is valid'
            ) : (
              <>
                Must be on or after the inventory date. {complexConfig.reason}.
                Update{' '}
                <Link
                  href={`/assets/${props.assetKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  the details for {props.assetSerial}
                  <OpenInNew fontSize="inherit" />
                </Link>{' '}
                if needed.
              </>
            )}
          </Typography>
        </Alert>
      </Stack>
    </Paper>
  );
}

/**
 * Form component for resolving date-related asset exceptions.
 * Displays the inventory date once at the top (since all exceptions
 * compare against it), then shows each exception with its specific
 * date field that needs to be adjusted.
 *
 * Features:
 * - Simple dates with inline save buttons
 * - Complex dates with informational messages and links
 * - Per-field loading/success/error states
 * - Real-time validation feedback
 * - Material-UI DatePicker integration
 * - Independent field saves
 *
 * Usage example:
 * ```tsx
 * <DateExceptionResolutionForm
 *   exceptionTypes={['IN_SERVICE_DATE_BEFORE_INVENTORY']}
 *   control={control}
 *   assetKey="asset-123"
 * />
 * ```
 */
export function DateExceptionResolutionForm(props: Props) {
  // Watch form values for real-time validation
  const inventoryDate =
    useWatch({
      control: props.control,
      name: 'inventoryDate',
    }) ?? null;
  const inServiceDate =
    useWatch({
      control: props.control,
      name: 'inServiceDate',
    }) ?? null;

  // Separate simple and complex exceptions
  const simpleExceptions = props.exceptionTypes.filter((type) =>
    SIMPLE_EXCEPTION_TYPES.includes(type)
  );
  const complexExceptions = props.exceptionTypes.filter((type) =>
    COMPLEX_EXCEPTION_TYPES.includes(type)
  );

  // Callback for handling successful saves
  const handleSaveSuccess = () => {
    // Additional success handling can be added here if needed
  };

  return (
    <Stack spacing={3}>
      {/* Inventory date section - shown once for all exceptions */}
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
          backgroundColor: 'primary.50',
          borderColor: 'primary.main',
          borderWidth: 2,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'primary.main',
              }}
            >
              <CalendarToday />
            </Box>
            <Typography variant="subtitle2" fontWeight="medium">
              Inventory date
            </Typography>
          </Stack>

          <EditableDateField
            fieldName="inventoryDate"
            fieldLabel="Inventory date"
            control={props.control}
            assetKey={props.assetKey}
            originalValue={props.dateData.inventoryDate}
            onSaveSuccess={handleSaveSuccess}
          />

          <Alert severity="info" icon={false}>
            <Typography variant="body2">
              When asset entered inventory. Should be on or before all other
              dates.
            </Typography>
          </Alert>
        </Stack>
      </Paper>

      {/* Simple exceptions - editable with inline save buttons */}
      {simpleExceptions.map((exceptionType) => {
        const config = getExceptionConfig(exceptionType);

        const resolved = isExceptionResolved(inventoryDate, inServiceDate);

        const iconColor = resolved ? 'success.main' : 'error.main';
        const borderColor = resolved ? 'success.main' : 'error.main';
        const backgroundColor = resolved ? 'success.50' : 'grey.50';

        return (
          <Paper
            key={exceptionType}
            variant="outlined"
            sx={{
              padding: 2,
              backgroundColor,
              borderColor,
              borderWidth: 2,
            }}
          >
            <Stack spacing={2}>
              {/* Exception header with icon and label */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: iconColor,
                  }}
                >
                  {config.icon}
                </Box>
                <Typography variant="subtitle2" color={iconColor}>
                  {config.label}
                </Typography>
              </Stack>

              {/* Editable in-service date field with save button */}
              <EditableDateField
                fieldName="inServiceDate"
                fieldLabel="In-service date"
                control={props.control}
                assetKey={props.assetKey}
                originalValue={props.dateData.inServiceDate}
                onSaveSuccess={handleSaveSuccess}
              />

              {/* Helper text with validation status */}
              <Alert
                severity={resolved ? 'success' : 'error'}
                icon={resolved ? <CheckCircle /> : <Error />}
              >
                <Typography variant="body2">
                  {resolved
                    ? 'Date is valid'
                    : 'Must be on or after the inventory date'}
                </Typography>
              </Alert>
            </Stack>
          </Paper>
        );
      })}

      {/* Complex exceptions - informational messages only */}
      {complexExceptions.map((exceptionType) => {
        // Get the appropriate date for this exception type
        const exceptionDate = (() => {
          switch (exceptionType) {
            case 'FIRST_SALE_DATE_BEFORE_INVENTORY':
              return props.dateData.firstSaleDate;
            case 'DISPOSAL_DATE_BEFORE_INVENTORY':
              return props.dateData.scrapDate;
            case 'FIRST_ASSIGNMENT_BEFORE_INVENTORY':
              return props.dateData.firstAssignmentDate;
            case 'FIRST_MOVEMENT_BEFORE_INVENTORY':
              return props.dateData.firstMovementDate;
            default:
              return null;
          }
        })();

        return (
          <ComplexDateExceptionMessage
            key={exceptionType}
            exceptionType={exceptionType}
            assetKey={props.assetKey}
            assetSerial={props.assetSerial}
            exceptionDate={exceptionDate}
            control={props.control}
          />
        );
      })}
    </Stack>
  );
}
